import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests
import EquipmentAddModal from './modals/Eqipment/EqipmentAddModel'; // Modal for adding/editing equipment
import EquipmentChecklist from './modals/Eqipment/EquipmentChecklist/EquipmentChecklistModal'; // Modal for starting equipment checklists
import EquipmentChecklistHistoryModal from './modals/Eqipment/EquipmentChecklist/EquipmentChecklistHistoryModal'; // Modal for viewing checklist history
import EquipmentChecklistHistoryListModal from './modals/Eqipment/EquipmentChecklist/EquipmentChecklistHistoryListModal'; // Modal for checklist history list
import ManageEquipment from './modals/Eqipment/EquipmentChecklist/ManageEquipment/ManageEquiement'; // Modal for managing equipment checklists
import Pagination from '../Tools/Pagination'; // Component for pagination controls

// EquipmentTab component for managing EMS equipment
const EquipmentTab = () => {
  // State management for equipment data and UI
  const [equipment, setEquipment] = useState([]); // Stores the list of equipment
  const [showModal, setShowModal] = useState(false); // Controls visibility of add/edit modal
  const [showHistoryModal, setShowHistoryModal] = useState(false); // Controls visibility of detailed history modal
  const [showManageModal, setShowManageModal] = useState(false); // Controls visibility of manage checklist modal
  const [currentHistoryEquipmentId, setCurrentHistoryEquipmentId] = useState(null); // ID of equipment for history modal
  const [currentManageEquipmentId, setCurrentManageEquipmentId] = useState(null); // ID of equipment for manage modal
  const [refreshHistory, setRefreshHistory] = useState(false); // Trigger to refresh history view

  const [formData, setFormData] = useState({
    name: '', // Equipment name
    eq_category: '', // Equipment category
    location_id: '', // Location ID
    area_id: '', // Area ID
    medical_center_id: '' // Medical center ID
  }); // Form data for equipment creation/editing

  const [isEditMode, setIsEditMode] = useState(false); // Tracks if modal is in edit mode
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [currentChecklistEquipment, setCurrentChecklistEquipment] = useState(null); // ID of equipment for checklist modal
  const [currentEquipmentId, setCurrentEquipmentId] = useState(null); // ID of equipment being edited

  const [search, setSearch] = useState(''); // Search query for filtering equipment
  const [page, setPage] = useState(1); // Current page for pagination
  const limit = 10; // Number of items per page
  const roleName = sessionStorage.getItem("Role_Name");

  // Get checklist type from sessionStorage
  const checklistType = sessionStorage.getItem('checklist_type');
  // Show checklist column: for "EMS Team", only when checklistType is 'equipment'; for other roles, always show
  const showChecklistColumn = roleName === 'EMS Team' ? checklistType === 'equipment' : true;

  /**
   * Displays an alert message to the user
   * @param {string} message - The message to display
   * @param {boolean} isSuccess - Whether it's a success message (default true)
   */
  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  /**
   * Fetches equipment from the API with role-based filtering
   * Applies client-side search filtering
   */
  const fetchEquipment = async () => {
    try {
      setIsLoading(true); // Set loading state
      const roleName = sessionStorage.getItem('Role_Name'); // Get user role
      const userId = sessionStorage.getItem('user_id') || ''; // Get user ID
      const params = {
        user_id: roleName === 'EMS Resource Manager' ? userId : '' // Conditional user_id
      };

      const response = await axiosInstance.get('ems/equipment', { params }); // Fetch equipment
      const data = response.data?.data || response.data || []; // Handle different response formats
      const filtered = data.filter(item =>
        item.name?.toLowerCase().includes(search.toLowerCase()) // Apply search filter
      );
      setEquipment(filtered); // Update equipment state
    } catch (error) {
      console.error('Error fetching equipment:', error); // Log error
      showAlert('Failed to fetch equipment', false); // Show error alert
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  // Fetch equipment when search query changes
  useEffect(() => {
    fetchEquipment();
  }, [search]); // Re-run when search changes

  /**
   * Handles input changes for the form
   * @param {Object} e - The event object
   */
  const handleChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFormData((prev) => ({ ...prev, [name]: value })); // Update form data
  };

  /**
   * Handles location selection, resets area_id
   * @param {string} locationId - The selected location ID
   */
  const handleLocationChange = (locationId) => {
    setFormData(prev => ({
      ...prev,
      location_id: locationId,
      area_id: '' // Reset area_id when location changes
    }));
  };

  /**
   * Handles area selection
   * @param {string} areaId - The selected area ID
   */
  const handleAreaChange = (areaId) => {
    setFormData(prev => ({
      ...prev,
      area_id: areaId // Update area_id
    }));
  };

  /**
   * Handles search input changes
   * @param {Object} e - The event object
   */
  const handleFilterChange = (e) => {
    const { value } = e.target; // Extract search value
    setSearch(value); // Update search query
    setPage(1); // Reset to first page
  };

  /**
   * Handles page change for pagination
   * @param {number} newPage - The new page number
   */
  const handlePageChange = (newPage) => {
    setPage(newPage); // Update current page
  };

  /**
   * Opens the modal for adding a new equipment
   * Resets form data and sets edit mode to false
   */
  const openAddModal = () => {
    setIsEditMode(false); // Set to add mode
    setFormData({
      name: '',
      eq_category: '',
      location_id: '',
      area_id: '',
      medical_center_id: '',
      eq_quantity: '', // Equipment quantity
      eq_limit: '' // Equipment limit
    }); // Reset form data
    setShowModal(true); // Show modal
  };

  /**
   * Prepares the modal for editing an existing equipment
   * @param {Object} equip - The equipment data to edit
   */
  const handleEdit = (equip) => {
    setIsEditMode(true); // Set to edit mode
    setCurrentEquipmentId(equip.id); // Set equipment ID
    setFormData({
      name: equip.name,
      eq_category: equip.eq_category,
      location_id: equip.location_id,
      area_id: equip.area_id,
      medical_center_id: equip.medical_center_id || '',
      eq_quantity: equip.eq_quantity || '',
      eq_limit: equip.eq_limit || ''
    }); // Populate form with equipment data
    setShowModal(true); // Show modal
  };

  /**
   * Handles saving or updating equipment
   * @param {Object} formDataFromModal - Form data from the modal
   */
  const handleSave = async (formDataFromModal) => {
    try {
      setIsLoading(true); // Set loading state
      const userId = sessionStorage.getItem('user_id') || ''; // Get user ID

      // Adjust payload based on selection
      let payload = {
        ...formDataFromModal,
        user_id: userId
      };

      if (formDataFromModal.medical_center_id) {
        // Medical Center chosen → reset location & area
        payload.location_id = '';
        payload.area_id = '';
      } else if (formDataFromModal.location_id) {
        // Location chosen → reset medical center
        payload.medical_center_id = '';
      }

      if (isEditMode) {
        await axiosInstance.put(`ems/equipment/${currentEquipmentId}`, payload); // Update equipment
        showAlert('Equipment updated successfully!'); // Show success alert
      } else {
        await axiosInstance.post('ems/equipment', payload); // Create equipment
        showAlert('Equipment added successfully!'); // Show success alert
      }

      setShowModal(false); // Close modal
      fetchEquipment(); // Refresh equipment list
    } catch (error) {
      console.error('Save failed:', error); // Log error
      showAlert(
        error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} equipment`, // Show error alert
        false
      );
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  /**
   * Deletes an equipment after confirmation
   * @param {number} id - The ID of the equipment to delete
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this equipment?')) {
      try {
        setIsLoading(true); // Set loading state
        await axiosInstance.delete(`ems/equipment/${id}`); // Delete equipment
        showAlert('Equipment deleted successfully!'); // Show success alert
        fetchEquipment(); // Refresh equipment list
      } catch (error) {
        console.error('Delete failed:', error); // Log error
        showAlert('Failed to delete equipment', false); // Show error alert
      } finally {
        setIsLoading(false); // Clear loading state
      }
    }
  };

  /**
   * Opens the checklist modal for an equipment
   * @param {number} equipmentId - The ID of the equipment
   */
  const handleOpenChecklist = (equipmentId) => {
    setCurrentChecklistEquipment(null); // Reset checklist equipment ID
    setTimeout(() => {
      setCurrentChecklistEquipment(equipmentId); // Set equipment ID for checklist
      const modalEl = document.getElementById('bs-daily-checklist');
      if (modalEl) {
        const bootstrapModal = new window.bootstrap.Modal(modalEl); // Initialize Bootstrap modal
        bootstrapModal.show(); // Show modal
      }
    }, 50); // Delay to ensure state reset
  };

  /**
   * Opens the history list modal for an equipment
   * @param {number} equipmentId - The ID of the equipment
   */
  const handleViewHistory = (equipmentId) => {
    sessionStorage.setItem('currentEquipmentId', equipmentId); // Store equipment ID in session
    setCurrentHistoryEquipmentId(equipmentId); // Set equipment ID for history
    setRefreshHistory(prev => !prev); // Toggle refresh trigger
    const modalEl = document.getElementById('bs-equipment-checklist-history-list');
    if (modalEl && window.bootstrap?.Modal) {
      const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl); // Initialize or get modal instance
      modal.show(); // Show modal
    } else {
      console.warn('Bootstrap modal not initialized properly.'); // Warn if modal fails
    }
  };

  /**
   * Opens the detailed history modal and hides the history list modal
   * @param {number} submissionId - The ID of the checklist submission
   */
  const handleOpenDetailedHistory = (submissionId) => {
    sessionStorage.setItem('currentSubmissionId', submissionId); // Store submission ID in session
    const listModalEl = document.getElementById('bs-equipment-checklist-history-list');
    if (listModalEl && window.bootstrap?.Modal) {
      const listModal = window.bootstrap.Modal.getInstance(listModalEl);
      if (listModal) listModal.hide(); // Hide history list modal
    }
    setShowHistoryModal(true); // Show detailed history modal
  };

  /**
   * Closes the history modal
   */
  const handleCloseHistory = () => {
    setShowHistoryModal(false); // Hide history modal
    setCurrentHistoryEquipmentId(null); // Clear history equipment ID
    sessionStorage.removeItem('currentSubmissionId'); // Clear submission ID from session
  };

  /**
   * Opens the manage checklist modal for an equipment
   * @param {number} equipmentId - The ID of the equipment
   */
  const handleManageChecklist = (equipmentId) => {
    sessionStorage.setItem('EquipmentId', equipmentId); // Store equipment ID in session
    setCurrentManageEquipmentId(equipmentId); // Set equipment ID for manage modal
    setShowManageModal(true); // Show manage modal
  };

  /**
   * Closes the manage checklist modal
   */
  const handleCloseManageModal = () => {
    sessionStorage.removeItem('EquipmentId'); // Clear equipment ID from session
    setShowManageModal(false); // Hide manage modal
    setCurrentManageEquipmentId(null); // Clear manage equipment ID
  };

  /**
   * Closes the add/edit modal and cleans up session storage
   */
  const handleModalClose = () => {
    setShowModal(false); // Hide add/edit modal
    sessionStorage.removeItem('EquipmentId'); // Clear equipment ID from session
  };

  // Pagination calculations
  const total = equipment.length; // Total number of equipment items
  const currentItems = equipment.slice((page - 1) * limit, page * limit); // Items for current page

  // Render the component
  return (
    <div className="tab-pane active" id="assets" role="tabpanel">
      {/* Filter Section */}
      <div className="filter-search mt-3">
        <div className="row mb-3">
          <h6 className="px-2">Filter</h6>
          <div className="col-md-3">
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search Equipment"
                value={search}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="col-md-8 text-end">
            {roleName !== "EMS Team" && (
              <button
                className="btn d-flex align-items-center justify-content-center btn-outline-primary ms-auto"
                type="button"
                onClick={openAddModal}
              >
                <i className="ti ti-circle-plus fs-4 me-2"></i>
                Add Equipment
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Equipment Table */}
      {isLoading ? (
        <div className="text-center py-4">Loading equipment...</div>
      ) : (
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  <th><h6 className="fw-semibold mb-0">ID</h6></th>
                  <th><h6 className="fw-semibold mb-0">Name</h6></th>
                  <th><h6 className="fw-semibold mb-0">Category</h6></th>
                  <th><h6 className="fw-semibold mb-0">Quantity</h6></th>
                  <th><h6 className="fw-semibold mb-0">Limit</h6></th>
                  <th><h6 className="fw-semibold mb-0">Location</h6></th>
                  <th><h6 className="fw-semibold mb-0">Area</h6></th>
                  <th><h6 className="fw-semibold mb-0">Medical Center</h6></th>
                  {/* Conditionally render checklist column header */}
                  {showChecklistColumn && (
                    <th colSpan="3" className="text-center"><h6 className="fw-semibold mb-0">Checklist</h6></th>
                  )}
                  {roleName !== "EMS Team" && <th><h6 className="fw-semibold mb-0">Action</h6></th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item.id}>
                      <td>{(page - 1) * limit + index + 1}</td> {/* Calculate display ID */}
                      <td>{item.name || 'N/A'}</td>
                      <td>{item.eq_category || 'N/A'}</td>
                      <td>{item.eq_quantity || 'N/A'}</td>
                      <td>{item.eq_limit || 'N/A'}</td>
                      <td>{item.ems_location?.location_name || 'N/A'}</td>
                      <td>{item.ems_area?.area_name || 'N/A'}</td>
                      <td>{item.medical_center?.name || 'N/A'}</td>
                      {/* Conditionally render checklist column */}
                      {showChecklistColumn && (
                        <td colSpan="3">
                          <div className="d-flex justify-content-center align-items-center">
                            <p className="mb-0 fw-normal">
                              <a className="text-primary" onClick={() => handleOpenChecklist(item.id)} style={{ cursor: 'pointer' }}>Start Checklist</a>
                              <span className="mx-1"> | </span>
                            </p>
                            <p className="mb-0 fw-normal">
                              <a className="text-primary" onClick={() => handleViewHistory(item.id)} style={{ cursor: 'pointer' }}>View History</a>
                            </p>
                            {roleName !== 'EMS Team' && (
                              <>
                                <span className='mx-1'> | </span>
                                <p className="mb-0 fw-normal">
                                  <a 
                                    className="text-primary" 
                                    onClick={() => handleManageChecklist(item.id)} 
                                    style={{ cursor: 'pointer' }}
                                  >
                                    Manage Checklist
                                  </a>
                                </p>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                      {roleName !== "EMS Team" && (
                        <td>
                          <button className="h4 me-3 border-0 bg-transparent" onClick={() => handleEdit(item)}>
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button className="h4 me-3 border-0 bg-transparent" onClick={() => handleDelete(item.id)}>
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showChecklistColumn ? 9 : 8 + (roleName !== "EMS Team" ? 1 : 0)} className="text-center py-4">
                      No equipment found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {equipment.length > 0 && (
            <Pagination
              total={total}
              limit={limit}
              page={page}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {/* Add/Edit Equipment Modal */}
      <EquipmentAddModal
        show={showModal}
        onClose={handleModalClose}
        formData={formData}
        onChange={handleChange}
        onLocationChange={handleLocationChange}
        onAreaChange={handleAreaChange}
        onSave={handleSave}
        isEditMode={isEditMode}
        isLoading={isLoading}
      />

      {/* Equipment Checklist Modal - Only render if checklist column is shown */}
      {showChecklistColumn && (
        <>
          <EquipmentChecklist
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
            vehicleId={currentChecklistEquipment}
          />
          <EquipmentChecklistHistoryListModal
            equipmentId={currentHistoryEquipmentId}
            onViewDetails={handleOpenDetailedHistory}
            refreshTrigger={refreshHistory}
          />
          {showHistoryModal && (
            <EquipmentChecklistHistoryModal
              equipmentId={currentHistoryEquipmentId}
              onClose={handleCloseHistory}
              refreshTrigger={refreshHistory}
            />
          )}
        </>
      )}

      {/* Manage Equipment Checklist Modal */}
      {showManageModal && (
        <ManageEquipment
          equipmentId={currentManageEquipmentId}
          onClose={handleCloseManageModal}
        />
      )}
    </div>
  );
};

// Export the component
export default EquipmentTab;